import { action, makeObservable, override } from 'mobx';
import { createContext, useContext } from 'react';
import ElectionsModule from 'store/modules/elections/electionsModule';
import IFullFormPageStoreWithElections from 'store/modules/full-employee-form/iFullFormPageStoreWithElections';
import { BaseFullEmployeeFormDomainParams } from 'store/modules/full-employee-form/types';
import QuoteFlowDelegate from 'store/flows/quote/quoteFlowDelegate';
import { redirectToRoute } from 'routes/redirectToRoute';
import { QuoteRouteV2Id } from 'routes/routesConfigV2';

export interface FullEmployeeFormDomainParams extends BaseFullEmployeeFormDomainParams {
  cartId: string;
}

export default class FullFormPageStore extends IFullFormPageStoreWithElections<FullEmployeeFormDomainParams> {
  public readonly flowDelegate: QuoteFlowDelegate;

  protected getPrimaryFormId(): string | undefined {
    return this._electionsModule.requiredSubInfo?.quotingSubFormId;
  }

  public override async getInitialData(): Promise<void> {
    const electionsModule = this._electionsModule;
    await this.flowDelegate.getFlowInitialData();
    await Promise.all([electionsModule.getRequiredSubInfo(), electionsModule.getProductLines()]);
    await super.getInitialData();
  }

  public get isInitialDataLoading(): boolean {
    return this.flowDelegate.isFlowDataLoading || super.isInitialDataLoading;
  }

  public onBackRedirect(): void {
    redirectToRoute(QuoteRouteV2Id.QUOTE_ADD_EMPLOYEES, {
      groupId: this._domainData.groupId,
      periodId: this._domainData.periodId,
      cartId: this._domainData.cartId,
    });
  }

  constructor(domainData: FullEmployeeFormDomainParams) {
    super(
      domainData,
      new ElectionsModule({
        groupId: domainData.groupId,
        enrollmentPeriodId: domainData.periodId,
        currentFlow: 'quoting',
        cartId: domainData.cartId,
      }),
    );

    this.flowDelegate = new QuoteFlowDelegate({
      domainParams: {
        groupId: domainData.groupId,
        periodId: domainData.periodId,
        cartId: domainData.cartId,
      },
      nextPageId: QuoteRouteV2Id.QUOTE_ADD_EMPLOYEES,
    });

    makeObservable<FullFormPageStore>(this, {
      onBackRedirect: action.bound,
      getInitialData: override,
    });
  }
}

const FullFormPageStoreContext = createContext(new FullFormPageStore({} as FullEmployeeFormDomainParams));

export const useFullFormPageStore = (): FullFormPageStore => {
  return useContext(FullFormPageStoreContext);
};

export { FullFormPageStoreContext };
